import { type ChatStatus, type UIMessage, getToolName, isToolUIPart } from "ai";
import { useEffect, useRef } from "react";
import { type BotLabComputedColumn, eventBus } from "$root/event-bus";
import { isPendingTaskPart } from "./conversationSync";

const RECALCULATE = "portfolio_lab-recalculate";

const COMPUTE_STATUSES: ReadonlySet<string> = new Set(["OK", "FALLBACK_USED", "FAILED", "SUPERSEDED", "QUEUED"]);

const isTurnStreaming = (status: ChatStatus) => status === "submitted" || status === "streaming";

type RecalculateInput = { columnUuids?: unknown; scenarioId?: unknown } | undefined;

function columnUuidsOf(input: RecalculateInput): string[] | null {
	const uuids = input?.columnUuids;
	return Array.isArray(uuids) ? uuids.filter((uuid): uuid is string => typeof uuid === "string") : null;
}

function scenarioIdOf(input: RecalculateInput): string | null {
	return typeof input?.scenarioId === "string" && input.scenarioId !== "" ? input.scenarioId : null;
}

/** The per-column list of a settled recalculate, at `structuredContent.original.computed` as sphere-mcp stores it. */
function computedColumns(output: unknown): BotLabComputedColumn[] {
	const computed = (output as { structuredContent?: { original?: { computed?: unknown } } } | undefined)
		?.structuredContent?.original?.computed;
	if (!Array.isArray(computed)) {
		return [];
	}
	return computed.flatMap((line: { columnUuid?: unknown; status?: unknown }) =>
		typeof line?.columnUuid === "string" && typeof line.status === "string" && COMPUTE_STATUSES.has(line.status)
			? [{ columnUuid: line.columnUuid, status: line.status as BotLabComputedColumn["status"] }]
			: [],
	);
}

/**
 * Tell the Portfolio Lab when SphereAI's `portfolio_lab-recalculate` starts and when its task settles
 * (MDOT-25847), so the page shows the columns calculating and ends them on the task's own result.
 *
 * The start is `bot-lab-recalculate-start`, once per call whose complete input first appears while a
 * turn of this tab is streaming, or in the render that ends it; a call restored while idle is history.
 * The end is `bot-lab-recalculate-settled`, once, when a call this tab started or saw working is
 * finished: the live stream holds the task's working ack and the chat's poll swaps in the stored part,
 * which keeps its `toolCallId`. So a compute that ends before the poll first reads it is still seen
 * ending. A task that failed as a whole settles with no columns.
 */
export function useBotLabRecalculate({ messages, status }: { messages: UIMessage[]; status: ChatStatus }): void {
	const seenToolCallIds = useRef(new Set<string>());
	const awaitedToolCallIds = useRef(new Set<string>());
	const previousStatus = useRef(status);

	useEffect(() => {
		const live = isTurnStreaming(status) || isTurnStreaming(previousStatus.current);
		previousStatus.current = status;
		for (const message of messages) {
			if (message.role !== "assistant") continue;
			for (const part of message.parts) {
				if (!isToolUIPart(part) || getToolName(part) !== RECALCULATE || part.state === "input-streaming") continue;
				const { toolCallId } = part;
				const input = part.input as RecalculateInput;
				if (!seenToolCallIds.current.has(toolCallId)) {
					seenToolCallIds.current.add(toolCallId);
					if (live) {
						awaitedToolCallIds.current.add(toolCallId);
						eventBus.emit("bot-lab-recalculate-start", {
							toolCallId,
							columnUuids: columnUuidsOf(input),
							scenarioId: scenarioIdOf(input),
						});
					}
				}
				if (isPendingTaskPart(part)) {
					awaitedToolCallIds.current.add(toolCallId);
					continue;
				}
				if (part.state === "input-available" || !awaitedToolCallIds.current.delete(toolCallId)) continue;
				eventBus.emit("bot-lab-recalculate-settled", {
					toolCallId,
					scenarioId: scenarioIdOf(input),
					columns: part.state === "output-error" ? null : computedColumns(part.output),
				});
			}
		}
	}, [messages, status]);
}
